import {useStaticQuery, graphql, Link} from 'gatsby';
import React, {useState} from 'react';
import {styled, useStyletron} from 'baseui';
import {GatsbyImage} from 'gatsby-plugin-image';
import {MdMenu, MdClose} from 'react-icons/md';
import useScrollPosition from '@react-hook/window-scroll';
import RegionSelector from '../storeComponents/RegionSelector';
import OrderNowButton from '../storeComponents/OrderNowButton';
import core from '../svg/core.svg';
import display from '../svg/display.svg';
import display2 from '../svg/display2.svg';
import newsOutline from '../svg/newsOutline.svg';
import {handleMixpanelAnalytics, MixpanelEvents} from '../utils/events';
import {
  ConfiguratorV2ProductIds,
  getConfiguratorProductName,
} from '../utils/storeConfigurator';
import {Pressable} from '../landingPageSections/support/MainShortcuts';

const fitWidth = '1167px';

const SCROLL_DIRECTION = {
  UNKNOWN: 'UNKNOWN',
  DOWN: 'DOWN',
  UP: 'UP',
};

const HeaderWrapper = styled<'div', {$isHidden: boolean}>(
  'div',
  ({$isHidden, $theme}) => ({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: $theme.colors.primary200,
    position: 'fixed',
    transition: 'transform 0.3s ease',
    zIndex: 96,
    transform: $isHidden
      ? 'translate3d(0px, -58px, 0px)'
      : 'translate3d(0px, 0px, 0px)',
  })
);

const SubMenuWrapper = styled<'div', {$isHidden: boolean}>(
  'div',
  ({$isHidden, $theme}) => ({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: $theme.colors.primary200,
    position: 'fixed',
    top: '58px',
    transition: 'transform 0.3s ease',
    zIndex: 93,
    transform: $isHidden
      ? 'translate3d(0px, -58px, 0px)'
      : 'translate3d(0px, 0px, 0px)',
  })
);

const HeaderContent = styled('div', () => ({
  display: 'flex',
  height: '58px',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const MobileMenuExtendedWrapper = styled<'div', {$isActive: boolean}>(
  'div',
  ({$theme, $isActive}) => ({
    position: 'fixed',
    top: '58px',
    left: '0px',
    width: '100%',
    backgroundColor: $theme.colors.primary200,
    zIndex: 94,
    transform: $isActive ? 'translate3d(0,0,0)' : 'translate3d(0,-700px,0)',
    transitionDuration: '500ms',
    display: 'none',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '32px 8px 64px 8px',
    '@media screen and (max-width: 1000px)': {
      display: 'flex',
    },
  })
);

const ImageWrapper = styled(Link, ({$theme}) => ({
  width: '150px',
  marginLeft: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  [$theme.mediaQuery.small]: {
    maxWidth: '50%',
    marginLeft: '5px',
  },
}));

const StyledCtaButton = styled(Link, ({$theme}) => ({
  flex: 0,
  borderRadius: '4px',
  textDecoration: 'none',
  padding: '5px 12px',
  backgroundColor: $theme.colors.accent,
  color: $theme.colors.backgroundPrimary,
  fontSize: '14px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  border: `1px solid ${$theme.colors.accent}`,
}));

const StyledMenuButton = styled(Link, ({$theme}) => ({
  padding: '4px 0',
  fontWeight: 500,
  margin: '0',
  fontSize: '16px',
  backgroundColor: 'transparent',
  whiteSpace: 'pre',
  transition: '200ms ease',
  borderBottom: '1px solid transparent',
  color: $theme.colors.backgroundPrimary,
  textDecoration: 'none',
  ':hover': {
    borderColor: $theme.colors.backgroundPrimary,
  },
  '@media screen and (max-width: 1000px)': {
    padding: '0',
    margin: '24px 0',
  },
}));

const MenuWrapperDesktop = styled('div', ({$theme}) => ({
  display: 'flex',
  flex: 1,
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '58px',
  marginRight: '16px',
  [$theme.mediaQuery.large]: {
    marginRight: '16px',
  },
  '@media screen and (max-width: 1000px)': {
    display: 'none',
  },
}));

const SubMenuWrapperDesktop = styled<'div', {$isActive: boolean}>(
  'div',
  ({$theme, $isActive}) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    height: '51px',
    padding: '5px 16px',
    maxWidth: '100%',
    background: $theme.colors.backgroundPrimary,
    boxShadow: '0px 4px 19px rgba(0, 0, 0, 0.1)',
    [$theme.mediaQuery.large]: {
      marginRight: '62px',
      boxShadow: '0px 4px 19px rgba(0, 0, 0, 0.1)',
    },
    [`@media (max-width: ${fitWidth})`]: {
      padding: '5px 8px',
      boxShadow: $isActive ? 'none' : '0px 4px 19px rgba(0, 0, 0, 0.1)',
    },
  })
);

const OrderNowWrapper = styled('div', () => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-end',
  flex: 1,
}));

const OrderNowPrice = styled('strong', () => ({
  margin: '0',
  padding: '0',
  marginRight: '14px',
  fontSize: '18px',
  fontWeight: 600,
}));

const MenuWrapperMobile = styled('div', () => ({
  display: 'none',
  margin: '0 16px 0 0',
  flexDirection: 'row',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: '16px',
  '@media screen and (max-width: 1000px)': {
    display: 'flex',
    flex: 1,
  },
  '@media (max-width: 310px)': {
    gap: '0px',
  },
}));

const ButtonWrapper = styled('div', ({$theme}) => ({
  flexDirection: 'row',
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  textAlign: 'center',
  [$theme.mediaQuery.large]: {
    gap: '32px',
  },
}));

const RegionAndCtaWrapper = styled('div', ({$theme}) => ({
  width: '230px',
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: '30px',
  [$theme.mediaQuery.medium]: {
    gap: '8px',
  },
  [$theme.mediaQuery.small]: {
    width: 'auto',
    maxWidth: '100%',
  },
}));

const RegionAndCtaWrapperSubmenu = styled('div', ({$theme}) => ({
  [$theme.mediaQuery.small]: {
    margin: '34px 0 0 0',
    padding: '14px 0 0 0',
    borderTop: `1px solid ${$theme.colors.backgroundPrimary}`,
    width: '100%',
  },
}));

const NewWrapper = styled('p', () => ({
  marginLeft: '8px',
  height: '20px',
  textAlign: 'center',
  borderRadius: '4px',
  marginBottom: '2px',
  padding: '2px',
  border: `1px solid #BF4800`,
  color: '#BF4800',
  fontSize: '12px',
  fontWeight: 700,
}));

const MenuButtonWrapper = styled('div', ({$theme}) => ({
  display: 'flex',
  margin: '0 16px',
  flexDirection: 'row',
  alignItems: 'center',
  [$theme.mediaQuery.small]: {
    margin: '0 16px',
  },
}));

const SvgIcon = styled('img', () => ({
  width: '24px',
  height: '24px',
  margin: '0 8px 3px 0',
  padding: 0,
}));

type CtaProps = {
  callToAction: string | null;
};

function RenderCta({callToAction}: CtaProps) {
  return (
    <StyledCtaButton to='/product-configuration/'>
      {callToAction}
    </StyledCtaButton>
  );
}

type Props = {
  isStorePage?: boolean;
  removeHeader?: boolean;
  hideNavigation?: boolean;
  skuIdentifier?: string;
  onMenuPress?: () => any;
  generic?: any;
  hideNavigationButtons?: boolean;
  priceString?: string;
  onOrderPress?: () => any;
  isLoading?: boolean;
  canCheckoutViaConfigurator?: boolean;
  subMenuTitle?: string;
  regionChangeTrigger?: string;
};

const getIconForIconString = (iconString: string) => {
  switch (iconString) {
    case 'core':
      return core;
    case 'display':
      return display;
    case 'newsOutline':
      return newsOutline;
    default:
      return display2;
  }
};

function Header({
  isStorePage,
  onMenuPress,
  hideNavigation,
  generic,
  priceString,
  onOrderPress,
  isLoading,
  canCheckoutViaConfigurator,
  regionChangeTrigger,
  subMenuTitle,
  removeHeader,
  hideNavigationButtons,
}: Props) {
  const data = useStaticQuery<GatsbyTypes.HeaderQueryQuery>(graphql`
    query HeaderQuery {
      contentfulAsset(title: {eq: "orca-mark-negative"}) {
        id
        title
        description
        gatsbyImageData(width: 240)
      }
      contentfulHeaderAndFooter {
        isNew
        callToAction
        jobsPageTitle
        home
        headerLinks {
          data {
            link
            name
            isNew
            icon
          }
        }
        storeSections {
          Overview {
            link
            name
          }
          Satisfaction_Guarantee {
            link
            name
          }
          In_the_box {
            link
            name
          }
          FAQ {
            link
            name
          }
        }
      }
      allContentfulPage(filter: {visibleInHeader: {eq: true}}) {
        edges {
          node {
            title
            slug
            visibleInHeader
          }
        }
      }
    }
  `);

  const onClick = (link: string) => {
    const header = 'Header';

    if (link === '/orca-display-2/') {
      handleMixpanelAnalytics(MixpanelEvents.PRODUCT_VIEWED, {
        product_name: getConfiguratorProductName(
          ConfiguratorV2ProductIds['orca-display-2']
        ),
        product_page_trigger_point: header,
      });
    } else if (link === '/orca-core/') {
      handleMixpanelAnalytics(MixpanelEvents.PRODUCT_VIEWED, {
        product_name: getConfiguratorProductName(
          ConfiguratorV2ProductIds['orca-core-1']
        ),
        product_page_trigger_point: header,
      });
    } else if (link === '/orca-experience/') {
      handleMixpanelAnalytics(MixpanelEvents.WHY_ORCA_ENTERED, {
        why_orca_trigger_point: header,
      });
    }
  };

  const scroll = useScrollPosition(5);
  const [, theme] = useStyletron();
  const [prevScroll, setPrevScroll] = useState(0);
  const [scrollDirection, setScrollDirection] = useState(
    SCROLL_DIRECTION.UNKNOWN
  );

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
  const [menuIconOpen, setMenuIconOpen] = useState(false);

  const {gatsbyImageData, description} = data.contentfulAsset!;

  React.useEffect(() => {
    if (scroll > prevScroll && scrollDirection !== SCROLL_DIRECTION.DOWN) {
      setScrollDirection(SCROLL_DIRECTION.DOWN);
    }

    if (scroll < prevScroll && scrollDirection !== SCROLL_DIRECTION.UP) {
      setScrollDirection(SCROLL_DIRECTION.UP);
    }

    if (isMenuOpen) {
      setIsMenuOpen(false);
    }

    if (isSubMenuOpen) {
      setIsSubMenuOpen(false);
    }

    setPrevScroll(scroll);
  }, [scroll]);

  const {callToAction, home, headerLinks} = data.contentfulHeaderAndFooter!;
  const isHiddenHeader =
    scroll > 0 && scrollDirection === SCROLL_DIRECTION.DOWN;

  const onMenuClick = () => {
    // Separate menu icon state to accomodate icon state
    // in Product Details guide
    setMenuIconOpen(!menuIconOpen);

    if (onMenuPress) {
      onMenuPress();
      return;
    }

    setIsMenuOpen(!isMenuOpen);
  };

  const links = headerLinks!.data!;

  const canCheckout = canCheckoutViaConfigurator;

  if (removeHeader) return null;

  return (
    <>
      {/* Vertical mobile menu */}
      <MobileMenuExtendedWrapper $isActive={isMenuOpen}>
        <MenuButtonWrapper>
          <StyledMenuButton to='/'>{home}</StyledMenuButton>
        </MenuButtonWrapper>
        {links.map(({name, link, isNew, icon}) => (
          <Pressable onClick={() => onClick(link)} key={name}>
            <MenuButtonWrapper>
              {icon ? <SvgIcon src={getIconForIconString(icon)} /> : null}
              <StyledMenuButton to={link}>{name}</StyledMenuButton>
              {isNew ? (
                <NewWrapper>{data.contentfulHeaderAndFooter!.isNew}</NewWrapper>
              ) : null}
            </MenuButtonWrapper>
          </Pressable>
        ))}
        {!isStorePage && isMenuOpen && (
          <RegionAndCtaWrapperSubmenu>
            <RegionSelector
              regionChangeTrigger={regionChangeTrigger}
              isStorePage={isStorePage}
            />
          </RegionAndCtaWrapperSubmenu>
        )}
      </MobileMenuExtendedWrapper>

      {/* Horizontal desktop / tablet / mobile menu */}
      <HeaderWrapper $isHidden={Boolean(isStorePage && isHiddenHeader)}>
        <HeaderContent>
          <ImageWrapper to='/'>
            <GatsbyImage
              loading='eager'
              image={gatsbyImageData!}
              alt={description!}
              imgStyle={{
                objectFit: 'contain',
                width: '128px',
              }}
            />
          </ImageWrapper>

          {/* Menu buttons desktop / tablet */}
          {!hideNavigation ? (
            <MenuWrapperDesktop>
              <ButtonWrapper>
                {!hideNavigationButtons &&
                  links.map(({name, link, isNew, icon}: any) => (
                    <Pressable onClick={() => onClick(link)} key={name}>
                      <MenuButtonWrapper>
                        {icon ? (
                          <SvgIcon src={getIconForIconString(icon)} />
                        ) : null}
                        <StyledMenuButton to={link}>{name}</StyledMenuButton>
                        {isNew ? (
                          <NewWrapper>
                            {data.contentfulHeaderAndFooter!.isNew}
                          </NewWrapper>
                        ) : null}
                      </MenuButtonWrapper>
                    </Pressable>
                  ))}
              </ButtonWrapper>
              <RegionAndCtaWrapper>
                {isStorePage ? (
                  <RegionSelector
                    regionChangeTrigger={regionChangeTrigger}
                    isStorePage={isStorePage}
                  />
                ) : (
                  <RenderCta callToAction={callToAction} />
                )}
              </RegionAndCtaWrapper>
            </MenuWrapperDesktop>
          ) : null}

          {/* Menu buttons mobile */}
          <MenuWrapperMobile>
            {isStorePage ? (
              <RegionSelector
                regionChangeTrigger={regionChangeTrigger}
                isStorePage={isStorePage}
              />
            ) : !hideNavigation ? (
              <RegionAndCtaWrapper>
                <RenderCta callToAction={callToAction} />
              </RegionAndCtaWrapper>
            ) : null}
            {isMenuOpen ? (
              <MdClose color='white' size='40px' onClick={onMenuClick} />
            ) : (
              <MdMenu color='white' size='40px' onClick={onMenuClick} />
            )}
          </MenuWrapperMobile>
        </HeaderContent>
      </HeaderWrapper>

      {/* Sub menu desktop / mobile */}
      <SubMenuWrapper $isHidden={Boolean(isStorePage && isHiddenHeader)}>
        {!hideNavigation && isStorePage ? (
          <SubMenuWrapperDesktop $isActive={isSubMenuOpen}>
            <OrderNowWrapper>
              <OrderNowPrice>{priceString}</OrderNowPrice>
              {canCheckout ? (
                <OrderNowButton
                  isLoading={Boolean(isLoading)}
                  onOrderPress={onOrderPress}
                  buttonTitle={
                    generic && generic.orderNowButton
                      ? generic.orderNowButton
                      : ''
                  }
                  theme={theme}
                  small
                />
              ) : null}
            </OrderNowWrapper>
          </SubMenuWrapperDesktop>
        ) : null}
      </SubMenuWrapper>
    </>
  );
}

export default Header;
